// ============================================================
// frontend/src/components/SaveButton.tsx
// ============================================================
// Animated save button. Three states:
//   - idle:    shows children (e.g., "Save changes")
//   - saving:  disabled, spinner + savingText
//   - saved:   green "Saved" with pulse animation
//
// Pair with `useSaving` hook for the full pattern.
// ============================================================

import type { ReactNode } from 'react';
import { Loader2, Check } from 'lucide-react';

interface SaveButtonProps {
  saving: boolean;
  saved: boolean;
  onClick?: () => void;
  disabled?: boolean;
  children?: ReactNode;
  /** Extra class names (e.g., to swap btn-primary for btn-accent) */
  variant?: 'primary' | 'danger';
  type?: 'button' | 'submit';
  savingText?: string;
  savedText?: string;
}

export function SaveButton({
  saving,
  saved,
  onClick,
  disabled = false,
  children = 'Save',
  variant = 'primary',
  type = 'button',
  savingText = 'Saving…',
  savedText = 'Saved',
}: SaveButtonProps) {
  // Button is disabled in three cases:
  //   1. saving — primary defense against double-click
  //   2. saved — brief lock so the user doesn't immediately re-click
  //   3. caller-provided disabled (form validation failed, etc.)
  const isDisabled = saving || saved || disabled;
  const baseClass = variant === 'danger' ? 'btn-danger' : 'btn-primary';
  const className = `${baseClass} ${saved ? 'is-saved' : ''}`;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      className={className}
      style={{ minWidth: 110 }}
    >
      {saving && (
        <>
          <Loader2 size={16} className="spin" />
          <span>{savingText}</span>
        </>
      )}
      {saved && !saving && (
        <>
          <Check size={16} />
          <span>{savedText}</span>
        </>
      )}
      {!saving && !saved && children}
    </button>
  );
}
